import { get, post, del, patch } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

export async function getClientList() {
    try {
        const session = await fetchAuthSession();

        const restOperation = get({
            apiName: 'gtms4',
            path: '/clients',
            options: {
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        });

        //console.log("Fetching client list from API Gateway...");
        const { body } = await restOperation.response
        const response = await body.json();

        return response;
    } catch (e) {
        try {
            console.log('POST call failed: ', JSON.parse(e.response.body));
        } catch {
            console.log('POST call failed: ', e);
        }
        return false;
    }
}

export async function getClientStatus(deviceID) {
    try {
        const session = await fetchAuthSession();

        const restOperation = get({
            apiName: 'gtms4',
            path: '/clients/status',
            options: {
                queryParams: {
                    client_id: deviceID
                },
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        });

        //console.log("Fetching client status from API Gateway for device:", deviceID);
        const { body } = await restOperation.response
        const response = await body.json();

        return response;
    } catch (e) {
        try {
            console.log('POST call failed: ', JSON.parse(e.response.body));
        } catch {
            console.log('POST call failed: ', e);
        }
        return false;
    }
}

async function handleCommand(deviceID, command) {
    console.log("Sending command to API Gateway:", command);
    const session = await fetchAuthSession();
    try {
        await post({
            apiName: 'gtms4',
            path: '/clients/command',
            options: {
                body: {
                    client_id: deviceID,
                    changed: command
                },
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        }).response;
    } catch (error) {
        try {
            const body = JSON.parse(error.response.body);
            throw new Error(body.error ?? 'Command failed.');
        } catch (parseError) {
            if (parseError instanceof SyntaxError) throw new Error('Command failed.');
            throw parseError;
        }
    }
}

export async function handleImageSelect(image, deviceID, boardID) {
    console.log("Selected image NG:", image);
    await handleCommand(deviceID, {
        image: {
            boardId: boardID,
            hash: "new_hash",
            name: image
        }
    });
}

export async function handleDisplayChange(displayState, deviceID) {
    console.log("Changing display state to:", displayState);
    handleCommand(deviceID, {
        display: displayState
    });
}

export async function handleLight1Change(light1State, deviceID) {
    console.log("Changing light 1 state to:", light1State);
    handleCommand(deviceID, {
        light_1: light1State
    });
}

export async function handleLight2Change(light2State, deviceID) {
    console.log("Changing light 2 state to:", light2State);
    handleCommand(deviceID, {
        light_2: light2State
    });
}

export async function getCompanies() {
    try {
        const session = await fetchAuthSession();
        const { body } = await get({
            apiName: 'gtms4',
            path: '/companies',
            options: {
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        }).response;
        return await body.json();
    } catch (e) {
        console.log('getCompanies failed: ', e);
        return [];
    }
}

export async function getUsers() {
    try {
        const session = await fetchAuthSession();
        const { body } = await get({
            apiName: 'gtms4',
            path: '/users',
            options: {
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        }).response;
        return await body.json();
    } catch (e) {
        console.log('getUsers failed: ', e);
        return [];
    }
}

export async function updateUser({ username, companyPath }) {
    try {
        const session = await fetchAuthSession();
        const { body } = await patch({
            apiName: 'gtms4',
            path: '/users',
            options: {
                body: { username, company_path: companyPath },
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        }).response;
        return await body.json();
    } catch (e) {
        try {
            console.log('updateUser call failed: ', JSON.parse(e.response.body));
        } catch {
            console.log('updateUser call failed: ', e);
        }
        return false;
    }
}

export async function deleteUser(username) {
    try {
        const session = await fetchAuthSession();
        const { body } = await del({
            apiName: 'gtms4',
            path: `/users/${encodeURIComponent(username)}`,
            options: {
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        }).response;
        return await body.json();
    } catch (e) {
        try {
            console.log('deleteUser call failed: ', JSON.parse(e.response.body));
        } catch {
            console.log('deleteUser call failed: ', e);
        }
        return false;
    }
}

export async function createUser({ email, name, company, companyPath }) {
    try {
        const session = await fetchAuthSession();
        const { body } = await post({
            apiName: 'gtms4',
            path: '/users',
            options: {
                body: { email, name, company, company_path: companyPath },
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        }).response;
        return await body.json();
    } catch (e) {
        try {
            console.log('createUser call failed: ', JSON.parse(e.response.body));
        } catch {
            console.log('createUser call failed: ', e);
        }
        return false;
    }
}

export async function getDevices() {
    try {
        const session = await fetchAuthSession();
        const { body } = await get({
            apiName: 'gtms4',
            path: '/devices',
            options: {
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        }).response;
        return await body.json();
    } catch (e) {
        console.log('getDevices failed: ', e);
        return [];
    }
}

export async function createDevice({ deviceID, deviceTypeId, path }) {
    try {
        const session = await fetchAuthSession();
        const { body } = await post({
            apiName: 'gtms4',
            path: '/devices',
            options: {
                body: { deviceID, deviceTypeId, path },
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        }).response;
        return await body.json();
    } catch (e) {
        try {
            console.log('createDevice call failed: ', JSON.parse(e.response.body));
        } catch {
            console.log('createDevice call failed: ', e);
        }
        return false;
    }
}

export async function updateDevice(deviceID, { path }) {
    try {
        const session = await fetchAuthSession();
        const { body } = await patch({
            apiName: 'gtms4',
            path: `/devices/${encodeURIComponent(deviceID)}`,
            options: {
                body: { path },
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        }).response;
        return await body.json();
    } catch (e) {
        console.log('updateDevice call failed: ', e);
        return false;
    }
}

export async function deleteImage(key) {
    const session = await fetchAuthSession();
    const { body } = await del({
        apiName: 'gtms4',
        path: '/image',
        options: {
            headers: {
                Authorization: `Bearer ${session.tokens.idToken}`
            },
            body: { key },
        }
    }).response;
    return body.json();
}

export async function getLogStreams() {
    try {
        const session = await fetchAuthSession();
        const { body } = await get({
            apiName: 'gtms4',
            path: '/logs',
            options: {
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        }).response;
        return await body.json();
    } catch (e) {
        console.log('getLogStreams failed: ', e);
        return [];
    }
}

export async function getLogEvents(clientId, nextToken = null, startTime = null, endTime = null) {
    try {
        const session = await fetchAuthSession();
        const queryParams = { client_id: clientId };
        if (nextToken) queryParams.next_token = nextToken;
        if (startTime) queryParams.start_time = String(startTime);
        if (endTime) queryParams.end_time = String(endTime);
        const { body } = await get({
            apiName: 'gtms4',
            path: '/logs',
            options: {
                queryParams,
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        }).response;
        return await body.json();
    } catch (e) {
        console.log('getLogEvents failed: ', e);
        return { events: [], nextToken: null };
    }
}

export async function deleteDevice(deviceID) {
    try {
        const session = await fetchAuthSession();
        const { body } = await del({
            apiName: 'gtms4',
            path: `/devices/${encodeURIComponent(deviceID)}`,
            options: {
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        }).response;
        return await body.json();
    } catch (e) {
        console.log('deleteDevice call failed: ', e);
        return false;
    }
}

export async function getDeviceSchedule(deviceID) {
    const session = await fetchAuthSession();
    try {
        const { body } = await get({
            apiName: 'gtms4',
            path: '/clients/schedule',
            options: {
                queryParams: { client_id: deviceID },
                headers: { Authorization: `Bearer ${session.tokens.idToken}` }
            }
        }).response;
        const data = await body.json();
        return data.schedule ?? null;
    } catch (e) {
        console.log('getDeviceSchedule failed: ', e);
        return null;
    }
}

export async function setDeviceSchedule(deviceID, schedule) {
    const session = await fetchAuthSession();
    try {
        const { body } = await post({
            apiName: 'gtms4',
            path: '/clients/schedule',
            options: {
                body: { client_id: deviceID, schedule },
                headers: { Authorization: `Bearer ${session.tokens.idToken}` }
            }
        }).response;
        return await body.json();
    } catch (e) {
        try {
            const b = JSON.parse(e.response.body);
            throw new Error(b.error ?? 'Failed to save schedule.');
        } catch (pe) {
            if (pe instanceof SyntaxError) throw new Error('Failed to save schedule.');
            throw pe;
        }
    }
}

export async function deleteDeviceSchedule(deviceID) {
    const session = await fetchAuthSession();
    try {
        const { body } = await del({
            apiName: 'gtms4',
            path: '/clients/schedule',
            options: {
                queryParams: { client_id: deviceID },
                headers: { Authorization: `Bearer ${session.tokens.idToken}` }
            }
        }).response;
        return await body.json();
    } catch (e) {
        console.log('deleteDeviceSchedule failed: ', e);
        return false;
    }
}

export async function getIotDevices() {
    try {
        const session = await fetchAuthSession();
        const { body } = await get({
            apiName: 'gtms4',
            path: '/admin/iot-devices',
            options: {
                headers: {
                    Authorization: `Bearer ${session.tokens.idToken}`
                }
            }
        }).response;
        return await body.json();
    } catch (e) {
        console.log('getIotDevices failed: ', e);
        return [];
    }
}